import { CheckCircleOutlineRounded, ErrorOutlineRounded } from '@mui/icons-material';
import { Alert, Box, Link, Stack, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import FormField from 'components/common/formikForm/formField';
import SubmitButton from 'components/common/formikForm/SubmitButton';
import AuthLayout, { authSubmitButtonSx } from 'views/v2/auth/components/authLayout';

import api from 'api';
import { useInflight } from 'hooks/useInflight';
import useTranslate from 'hooks/useTranslate';
import type { PasswordRecoveryInterface } from 'types';
import {
    isValidEmail,
    passwordRecoveryDefaultValues,
    passwordRecoveryValidationSchema,
} from 'utils/validations/auth';

export default function PasswordRecoveryV2() {
    const { translate } = useTranslate();
    const [searchParams] = useSearchParams();

    // Prefill from login when the user had a valid email before clicking
    // "Forgot your password?" (`/password-recovery?email=...`).
    const emailFromQuery = searchParams.get('email')?.trim() ?? '';
    const prefilledEmail = isValidEmail(emailFromQuery) ? emailFromQuery : '';

    const [sent, setSent] = useState(false);
    const [rateLimited, setRateLimited] = useState(false);

    // In-flight guard: a double-click on "Send" used to fire two
    // POST /forgot_password requests within ~100 ms. The hook drops the
    // second one until the first settles, then we show a persistent
    // confirmation panel with an explicit "Back to login" link.
    const { pending: sending, run: runSend } = useInflight(
        async (_signal, values: PasswordRecoveryInterface) => {
            setRateLimited(false);
            try {
                await api.forgotPassword.post({
                    email: values.email.trim().toLowerCase(),
                });
            } catch (err) {
                // 429 is the only outcome we surface differently — it's a
                // rate-limit, not a statement about whether the account exists.
                if ((err as AxiosError)?.response?.status === 429) {
                    setRateLimited(true);
                    setSent(true);
                    return;
                }
                // The endpoint responds identically for "user exists" and
                // "user not found" by design (anti-enumeration), so any other
                // error is treated the same as success from the UI's perspective.
            }
            setSent(true);
        }
    );

    const handleSubmit = async (values: PasswordRecoveryInterface) => {
        await runSend(values);
    };

    const title = !sent
        ? translate('forgotPasswordPrompt')
        : rateLimited
          ? translate('forgotPasswordRateLimitedTitle')
          : translate('forgotPasswordSuccessTitle');

    const subtitle = !sent ? translate('forgotPasswordSubtitle') : undefined;

    return (
        <AuthLayout
            title={title}
            subtitle={subtitle}
            footer={
                <Link
                    component={RouterLink}
                    to="/login"
                    underline="hover"
                    variant="body2"
                    sx={{ fontWeight: 600, color: 'primary.main', py: 0.5 }}
                >
                    {translate('backToLogin')}
                </Link>
            }
        >
            {!sent ? (
                <Formik
                    initialValues={{
                        ...passwordRecoveryDefaultValues,
                        email: prefilledEmail || passwordRecoveryDefaultValues.email,
                    }}
                    validationSchema={passwordRecoveryValidationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting }) => {
                        const busy = isSubmitting || sending;
                        return (
                            <Form noValidate aria-busy={busy}>
                                <Stack spacing={2}>
                                    <FormField
                                        name="email"
                                        label="email"
                                        type="email"
                                        disabled={busy}
                                        autoComplete="email"
                                        inputProps={{
                                            inputMode: 'email',
                                            autoCapitalize: 'none',
                                            autoCorrect: 'off',
                                        }}
                                    />

                                    <SubmitButton
                                        loading={busy}
                                        disabled={busy}
                                        fullWidth
                                        size="large"
                                        sx={authSubmitButtonSx}
                                    >
                                        send
                                    </SubmitButton>
                                </Stack>
                            </Form>
                        );
                    }}
                </Formik>
            ) : (
                <Stack spacing={2.5} sx={{ alignItems: 'center', textAlign: 'center' }}>
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: rateLimited
                                ? 'rgba(245, 158, 11, 0.12)'
                                : 'rgba(16, 185, 129, 0.12)',
                            color: rateLimited ? 'warning.main' : 'success.main',
                        }}
                    >
                        {rateLimited ? (
                            <ErrorOutlineRounded sx={{ fontSize: 36 }} />
                        ) : (
                            <CheckCircleOutlineRounded sx={{ fontSize: 36 }} />
                        )}
                    </Box>

                    {rateLimited ? (
                        <Alert
                            severity="warning"
                            variant="outlined"
                            sx={{
                                width: '100%',
                                borderRadius: 2,
                                textAlign: 'left',
                            }}
                        >
                            {translate('tooManyRequests')}
                        </Alert>
                    ) : (
                        <Stack spacing={1}>
                            <Typography
                                variant="body1"
                                color="text.primary"
                                sx={{ fontWeight: 500, lineHeight: 1.5 }}
                            >
                                {translate('forgotPasswordConfirmation')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {translate('checkSpamFolder')}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            )}
        </AuthLayout>
    );
}
